// src/components/ClientLogin.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { clientLogin, resetClientPassword } from '../api';

const ClientLogin: React.FC = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [mustResetPassword, setMustResetPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('clientToken');
    if (token) {
      // Check if the user must reset their password
      fetch(`${process.env.REACT_APP_API_BASE_URL}/api/ClientAuth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ username: localStorage.getItem('clientUsername'), password }),
      }).then(response => response.json()).then(data => {
        if (data.mustResetPassword) {
          setMustResetPassword(true);
        } else {
          navigate('/client/dashboard');
        }
      }).catch(() => {
        localStorage.removeItem('clientToken');
      });
    }
  }, [navigate]);

  const handleLogin = async () => {
    setIsLoading(true);
    setError('');
    try {
      const response = await clientLogin(username, password);
      if (response.mustResetPassword) {
        setMustResetPassword(true);
        localStorage.setItem('clientToken', response.token);
        localStorage.setItem('clientUsername', username);
      } else {
        localStorage.setItem('clientToken', response.token);
        localStorage.setItem('clientUsername', username);
        window.dispatchEvent(new Event('clientAuthChange'));
        navigate('/client/dashboard');
      }
    } catch (error) {
      console.error('Login failed:', error);
      setError('Login failed! Check your credentials.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async () => {
    setIsLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('clientToken');
      if (!token) throw new Error('No client authentication token found.');
      const response = await resetClientPassword(token, newPassword);
      localStorage.setItem('clientToken', response.token);
      window.dispatchEvent(new Event('clientAuthChange'));
      navigate('/client/dashboard');
    } catch (error) {
      console.error('Password reset failed:', error);
      setError('Failed to reset password. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="container mt-5">
      <div className="row justify-content-center">
        <div className="col-md-6">
          <div className="card shadow-sm">
            <div className="card-header bg-primary text-white text-center">
              <h4 className="mb-0">Client Login</h4>
            </div>
            <div className="card-body">
              {error && <div className="alert alert-danger">{error}</div>}
              {!mustResetPassword ? (
                <>
                  <div className="mb-3">
                    <label className="form-label">Username</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Enter username"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      disabled={isLoading}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Password</label>
                    <input
                      type="password"
                      className="form-control"
                      placeholder="Enter password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      disabled={isLoading}
                    />
                  </div>
                  <div className="d-flex justify-content-center">
                    <button
                      onClick={handleLogin}
                      className="btn btn-primary"
                      disabled={isLoading}
                    >
                      {isLoading ? 'Logging in...' : 'Login'}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="alert alert-info text-center">
                    You must reset your password before continuing.
                  </div>
                  <div className="mb-3">
                    <label className="form-label">New Password</label>
                    <input
                      type="password"
                      className="form-control"
                      placeholder="Enter new password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      disabled={isLoading}
                    />
                  </div>
                  <div className="d-flex justify-content-center">
                    <button
                      onClick={handleResetPassword}
                      className="btn btn-primary"
                      disabled={isLoading}
                    >
                      {isLoading ? 'Resetting...' : 'Reset Password'}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientLogin;